import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/mobile';

/**
 * Phone numbers for every country (the owner opened sign-in to everyone, 2026-10-20). libphonenumber's *mobile*
 * metadata, so "valid" means a valid mobile number for that country. Numbers go to the server as E.164
 * ("+919876543210").
 */
export type Country = { code: CountryCode; dial: string; name: string; flag: string };

const COUNTRY_KEY = 'zr-country';

/** 🇮🇳 from "IN": two regional-indicator letters. */
export const flagOf = (code: string) => String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

let names: Intl.DisplayNames | null = null;
const nameOf = (code: string) => {
  try {
    names ??= new Intl.DisplayNames(['en'], { type: 'region' });
    return names.of(code) ?? code;
  } catch {
    return code;
  }
};

let all: Country[] | null = null;
/** Every country libphonenumber knows, by name. */
export function countries(): Country[] {
  all ??= getCountries()
    .map((code) => ({ code, dial: `+${getCountryCallingCode(code)}`, name: nameOf(code), flag: flagOf(code) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return all;
}

export const isCountry = (v: unknown): v is CountryCode => typeof v === 'string' && (getCountries() as string[]).includes(v);
export const countryOf = (code: CountryCode): Country => countries().find((c) => c.code === code) ?? { code, dial: `+${getCountryCallingCode(code)}`, name: nameOf(code), flag: flagOf(code) };

const rank = (c: Country, s: string) => (c.code.toLowerCase() === s ? 0 : c.name.toLowerCase().startsWith(s) ? 1 : 2);

/** Search by name, ISO code or dialling code ("ind", "IN", "+91", "91"). */
export function searchCountries(q: string, list = countries()): Country[] {
  const s = q.trim().toLowerCase();
  if (!s) return list;
  const digits = s.replace(/^\+/, '');
  return list
    .filter((c) => c.name.toLowerCase().includes(s) || c.code.toLowerCase() === s || (/^\d+$/.test(digits) && c.dial.slice(1).startsWith(digits)))
    .sort((a, b) => rank(a, s) - rank(b, s));
}

/** The rider's last country, else the server's default, else India. */
export function startCountry(serverDefault?: string | null): CountryCode {
  try {
    const v = localStorage.getItem(COUNTRY_KEY);
    if (isCountry(v)) return v;
  } catch {
    /* ignore */
  }
  return isCountry(serverDefault) ? serverDefault : 'IN';
}
export const rememberCountry = (c: CountryCode) => {
  try {
    localStorage.setItem(COUNTRY_KEY, c);
  } catch {
    /* ignore */
  }
};

/** Most digits a national number can have (E.164 allows 15 including the country code). */
export const maxDigits = (c: CountryCode) => 15 - getCountryCallingCode(c).length;

/** The E.164 number when `national` is a valid mobile number for the country, else null. */
export function e164(national: string, c: CountryCode): string | null {
  const d = national.replace(/\D/g, '');
  if (!d) return null;
  const p = parsePhoneNumberFromString(d, c);
  return p && p.isValid() && p.country === c ? p.number : null;
}

/** Digits as the country writes them while typing ("98765 43210" in India). */
export const formatNational = (national: string, c: CountryCode) => (national ? new AsYouType(c).input(national.replace(/\D/g, '')) : '');

/** A pasted number with or without its country code → national digits for the country (and the country it names). */
export function fromPaste(text: string, c: CountryCode): { national: string; country: CountryCode } {
  const p = parsePhoneNumberFromString(text, c);
  if (p?.country) return { national: p.nationalNumber, country: p.country };
  return { national: text.replace(/\D/g, '').slice(0, maxDigits(c)), country: c };
}

/** "+91 98765 43210" for showing a stored E.164 number (or a legacy 10-digit Indian one). */
export function prettyPhone(n: string): string {
  const p = parsePhoneNumberFromString(n.startsWith('+') ? n : n, n.startsWith('+') ? undefined : 'IN');
  return p ? p.formatInternational() : n;
}

/** A plausible email address (the server has the last word). */
export const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());
